import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Download } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { imageSrc, sectionOf } from "@/lib/site";

export interface DuePin { id: string; article: string; headline: string; scheduled_for: string; image_path: string; rendered_at: string; post_slug?: string }
export interface DraftRow { slug: string; title: string; section: string; image?: string | null; approve_by?: string | null; is_new: boolean }

const short = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function PinRow({ p, today, onDone }: { p: DuePin; today: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const mark = async () => {
    setBusy(true);
    try { await api.patch(`/admin/pins/${p.id}`, { status: "posted" }); toast.success("Marked as posted"); onDone(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`home-pin-${p.id}`}>
      <img src={`${fileUrl(p.image_path)}?v=${p.rendered_at}`} alt="" className="h-16 w-11 shrink-0 rounded-lg object-cover" loading="lazy" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-[#23324A]">{p.headline}</p>
        <p className={`text-xs font-semibold ${p.scheduled_for < today ? "text-[#E84855]" : "text-[#23324A]/60"}`}>{p.scheduled_for < today ? `Overdue since ${short(p.scheduled_for)}` : "Due today"}</p>
      </div>
      <a className="btn-ghost" aria-label="Download pin" href={`${fileUrl(p.image_path)}?download=${p.post_slug || "pin"}.jpg`} download data-testid={`home-pin-download-${p.id}`}><Download className="h-4 w-4" /></a>
      <button className="btn-outline !px-3 !py-1.5 text-xs" disabled={busy} onClick={mark} data-testid={`home-pin-posted-${p.id}`}><Check className="h-3.5 w-3.5" strokeWidth={3} />Posted</button>
    </li>
  );
}

export function PinsDue({ pins, total, today, onDone }: { pins: DuePin[]; total: number; today: string; onDone: () => void }) {
  return (
    <section className="calm-card p-5 sm:p-6" data-testid="home-pins-due">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-black text-[#23324A]">Pins to post <span className="text-[#F08A6C]">({total})</span></h2>
        <Link to="/admin/pins" className="text-sm font-bold text-[#23324A] underline underline-offset-4" data-testid="home-all-pins">All pins</Link>
      </div>
      {pins.length ? <ul className="mt-2 divide-y divide-[#23324A]/10">{pins.map((p) => <PinRow key={p.id} p={p} today={today} onDone={onDone} />)}</ul>
        : <p className="mt-4 text-sm text-[#23324A]/60" data-testid="home-pins-empty">You're all caught up on pins.</p>}
    </section>
  );
}

export function DraftsList({ drafts, total, today }: { drafts: DraftRow[]; total: number; today: string }) {
  return (
    <section className="calm-card p-5 sm:p-6" data-testid="home-drafts">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-black text-[#23324A]">Drafts to approve <span className="text-[#F08A6C]">({total})</span></h2>
        <Link to="/admin/articles" className="text-sm font-bold text-[#23324A] underline underline-offset-4" data-testid="home-all-drafts">All drafts</Link>
      </div>
      {drafts.length ? (
        <ul className="mt-2 divide-y divide-[#23324A]/10">
          {drafts.map((d) => (
            <li key={d.slug}>
              <Link to={`/admin/articles?open=${d.slug}`} className="flex items-center gap-3 py-3 hover:opacity-80" data-testid={`home-draft-${d.slug}`}>
                {d.image ? <img src={imageSrc(d.image)} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" /> : <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#FDFBF7] text-[10px] font-bold text-[#23324A]/50">No photo</span>}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-[#23324A]">{d.title}</p>
                  <p className="text-xs font-semibold text-[#23324A]/60">{sectionOf(d.section)?.name || "No section"}{d.is_new ? " · new post" : " · refresh"}</p>
                </div>
                {d.approve_by && <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${d.approve_by <= today ? "bg-[#FDE6DF] text-[#B3261E]" : "bg-[#F6D776]/60 text-[#23324A]"}`}>by {short(d.approve_by)}</span>}
              </Link>
            </li>
          ))}
        </ul>
      ) : <p className="mt-4 text-sm text-[#23324A]/60">No drafts waiting.</p>}
    </section>
  );
}
